<!--
  "Jump to lot number" — see the component header for why this exists.

  `@if` / `@for` throughout and NOT one `<ng-container>` or `<ng-template>`:
  `npm run lint:templates` (the BXS-3584 guard) rejects host-element bindings on
  container nodes, and the built-in blocks make the whole class of mistake
  impossible rather than merely caught.

  The combobox is spelled out by hand rather than reached for from a UI kit.
  A `<datalist>` cannot show the horse's name beside the number, cannot mark a
  withdrawn lot, and renders differently in every browser; Material's autocomplete
  would pull an overlay module into two pages that use nothing else from it.
-->
@if (isUsable) {
  <div
    class="lot-jump"
    [class.is-ia]="variant === 'ia'"
    [class.is-buyer]="variant === 'buyer'"
  >
    <div class="lot-jump__row">
      <!-- The label WRAPS the field: the house's own word for a lot number is
           the field's name, and wrapping needs no id to associate the two. -->
      <label class="lot-jump__label">
        <span class="lot-jump__labelText">{{ lotLabel }}</span>

        <span class="lot-jump__control">
          <input
            #field
            class="lot-jump__field"
            type="text"
            role="combobox"
            dir="ltr"
            inputmode="numeric"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            [value]="text"
            [attr.aria-expanded]="open && suggestions.length > 0"
            [attr.aria-controls]="uid + '-list'"
            [attr.aria-activedescendant]="
              activeIndex >= 0 ? uid + '-opt-' + activeIndex : null
            "
            [attr.aria-invalid]="error !== null"
            [attr.aria-describedby]="error !== null ? uid + '-msg' : null"
            [attr.placeholder]="range"
            aria-autocomplete="list"
            (focus)="onFocus()"
            (blur)="onBlur($event)"
            (input)="onInput($event)"
            (keydown)="onKeydown($event)"
          />

          @if (open && suggestions.length > 0) {
            <ul class="lot-jump__list" [id]="uid + '-list'" role="listbox">
              @for (entry of suggestions; track entry.value; let i = $index) {
                <li
                  class="lot-jump__option"
                  [id]="uid + '-opt-' + i"
                  [class.is-active]="i === activeIndex"
                  [class.is-blocked]="entry.blocked"
                  role="option"
                  [attr.aria-selected]="i === activeIndex"
                  tabindex="-1"
                  (mousedown)="choose(entry)"
                >
                  <span class="lot-jump__optionNum" dir="ltr">{{ entry.value }}</span>
                  <span class="lot-jump__optionName" dir="auto">{{ entry.label }}</span>
                  @if (entry.tagKey) {
                    <span class="lot-jump__optionTag">{{
                      entry.tagKey | translate
                    }}</span>
                  }
                </li>
              }
            </ul>
          }
        </span>
      </label>

      <button
        type="button"
        class="lot-jump__go"
        [attr.aria-label]="'lot_jump.go' | translate"
        [disabled]="loading"
        (click)="submit()"
      >
        @if (loading) {
          <span class="lot-jump__spinner" aria-hidden="true"></span>
        } @else {
          <span class="lot-jump__goArrow" aria-hidden="true">&#8594;</span>
        }
      </button>
    </div>

    <!-- One live region for all four outcomes, so a screen reader announces the
         failure without the message shifting the row it sits under. -->
    <p
      class="lot-jump__msg"
      [class.is-empty]="error === null"
      [id]="uid + '-msg'"
      role="status"
      aria-live="polite"
    >
      @if (error === 'not_found') {
        {{
          'lot_jump.not_found'
            | translate: { label: lotLabel, value: errorValue }
        }}
      } @else if (error === 'blocked') {
        {{
          blockedMessageKey | translate: { label: lotLabel, value: errorValue }
        }}
      }
    </p>
  </div>
}
